import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, Send, Users, Copy, Check, Info } from 'lucide-react';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import { MattermostConfigForm } from '@/features/mattermost/components/MattermostConfigForm';
import { OnboardingUsersTable } from '@/features/mattermost/components/OnboardingUsersTable';
import { SendMessageDialog } from '@/features/mattermost/components/SendMessageDialog';
import { WaveSsoSection } from '@/features/mattermost/components/WaveSsoSection';
import { mattermostService } from '@/features/mattermost/services/mattermost.service';
import { usePermissions } from '@/common/hooks/usePermissions';
import { toast } from 'sonner';

export const Route = createFileRoute('/_authenticated/mattermost-config')({
  component: MattermostConfigPage,
});

function MattermostConfigPage() {
  const queryClient = useQueryClient();
  const { user } = usePermissions();
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendDialogMode, setSendDialogMode] = useState<'onboarding' | 'message'>('onboarding');
  const [tenantIdCopied, setTenantIdCopied] = useState(false);

  const {
    data: config,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['mattermost-config'],
    queryFn: () => mattermostService.getConfig(),
  });

  const handleSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: ['mattermost-config'] });
    await refetch();
  };

  const handleSendOnboarding = () => {
    setSendDialogMode('onboarding');
    setSendDialogOpen(true);
  };

  const handleSendMessage = () => {
    setSendDialogMode('message');
    setSendDialogOpen(true);
  };

  const copyTenantId = () => {
    if (user?.tenantId) {
      navigator.clipboard.writeText(user.tenantId);
      setTenantIdCopied(true);
      toast.success('Tenant ID copiado!');
      setTimeout(() => setTenantIdCopied(false), 2000);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <PermissionGate permission={Permission.MATTERMOST_CONFIG}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Configuração do Mattermost</h1>
            <p className="text-muted-foreground mt-2">
              Configure o bot de onboarding automático do Mattermost
            </p>
          </div>
        </div>

        {/* Informações para Configuração do Bot */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              Informações para Configuração do Bot
            </CardTitle>
            <CardDescription>
              Use estas informações para configurar o arquivo .env do bot do Mattermost
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border bg-muted p-4">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Tenant ID</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={copyTenantId}
                  disabled={!user?.tenantId}
                >
                  {tenantIdCopied ? (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Copiado
                    </>
                  ) : (
                    <>
                      <Copy className="mr-2 h-4 w-4" />
                      Copiar
                    </>
                  )}
                </Button>
              </div>
              <p className="font-mono text-xs text-muted-foreground break-all mb-2">
                {user?.tenantId || 'Carregando...'}
              </p>
              <p className="text-xs text-muted-foreground">
                Adicione no arquivo .env do bot como{' '}
                <code className="bg-background px-1 rounded">MATTERMOST_TENANT_ID</code>
              </p>
            </div>

            <div className="rounded-lg border bg-muted p-4">
              <div className="flex items-center gap-2 mb-2">
                <Label className="text-sm font-medium">API Key</Label>
              </div>
              {config ? (
                <>
                  <p className="text-xs text-muted-foreground mb-2">
                    A API key é gerada quando você cria a configuração ou clica em "Gerar Nova API
                    Key". Ela só é mostrada uma vez após a geração.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Adicione no arquivo .env do bot como{' '}
                    <code className="bg-background px-1 rounded">ASSETO_API_KEY</code>
                  </p>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Crie a configuração primeiro para gerar a API key. Ela será mostrada após a
                  criação.
                </p>
              )}
            </div>

            <div className="rounded-lg border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-950 p-4">
              <h4 className="font-medium text-sm mb-2">Exemplo de arquivo .env do bot:</h4>
              <pre className="text-xs bg-background p-3 rounded overflow-x-auto">
                {`# Mattermost Configuration
MATTERMOST_URL=http://localhost:8065
MATTERMOST_TOKEN=seu_token_do_bot_aqui
MATTERMOST_BOT_USER_ID=seu_bot_user_id_aqui

# Backend Asseto Configuration
ASSETO_BACKEND_URL=http://localhost:3000
ASSETO_API_KEY=sua_api_key_aqui
MATTERMOST_TENANT_ID=${user?.tenantId || 'seu_tenant_id_aqui'}`}
              </pre>
            </div>
          </CardContent>
        </Card>

        <MattermostConfigForm config={config ?? null} onSuccess={handleSuccess} />

        {config && <WaveSsoSection />}

        {config && (
          <>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Ações</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Enviar mensagens de onboarding ou mensagens customizadas
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSendOnboarding} variant="default">
                      <Users className="mr-2 h-4 w-4" />
                      Enviar Onboarding
                    </Button>
                    <Button onClick={handleSendMessage} variant="outline">
                      <Send className="mr-2 h-4 w-4" />
                      Enviar Mensagem
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <OnboardingUsersTable />
              </CardContent>
            </Card>

            <SendMessageDialog
              open={sendDialogOpen}
              onOpenChange={setSendDialogOpen}
              mode={sendDialogMode}
              onSuccess={handleSuccess}
            />
          </>
        )}
      </div>
    </PermissionGate>
  );
}
